import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import DataTable from "react-data-table-component";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import DownloadButtons from "../DownloadButtons/DownloadButtons";
import EntriesSelector from "../Pagination/EntriesSelector";
import Pagination from "../Pagination/Paginations";
import { API_BASE_URL } from "../../config";

const NoLeaveReport = () => {

  const today = new Date().toLocaleDateString("en-CA");

  const [filters, setFilters] = useState({
    from_date: today,
    to_date: today,
    dept_id: 0,
  });

  const [departments, setDepartments] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // ✅ Fetch Departments Only
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await axios.get(API_BASE_URL + "/showdepartment", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        });

        setDepartments(res.data.departments || res.data.data || res.data);
      } catch (err) {
        toast.error("Failed to load departments");
      }
    };

    fetchDepartments();
  }, []);

  // ✅ API Call Only When Clicking Button
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!filters.from_date || !filters.to_date) {
      toast.error("Please select From Date and To Date", { toastId: "no-leave-validation-date" });
      return;
    }

    if (filters.from_date > filters.to_date) {
      toast.error("From Date cannot be greater than To Date", { toastId: "no-leave-validation-range" });
      return;
    }

    // block repeat clicks while a request is already running
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    try {
      const res = await axios.post(
        API_BASE_URL + "/withoutleave",
        {
          from_date: filters.from_date,
          to_date: filters.to_date,
          dept_id: filters.dept_id,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      setRecords(res.data.records || []);
      toast.success("Report Loaded Successfully!", { toastId: "no-leave-success" });
    } catch (error) {
      toast.error("Error fetching report", { toastId: "no-leave-error" });
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  };

  const filteredRecordsAll = records.filter((item) =>
    `${item.employee_id} ${item.employee_name} ${item.department_name}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  /* Pagination (Previous / Next style) */
  const totalPages = Math.ceil(filteredRecordsAll.length / perPage);
  const indexOfLast = currentPage * perPage;
  const indexOfFirst = indexOfLast - perPage;
  const filteredRecords = filteredRecordsAll.slice(indexOfFirst, indexOfLast);

  const exportData = filteredRecordsAll.map(({ user_id, ...rest }) => ({
    ...rest,
    from_date: filters.from_date,
    to_date: filters.to_date,
  }));

  const columns = [
    {
      name: "Employee ID",
      selector: (row) => row.employee_id,
      sortable: true,
    },
    {
      name: "Employee Name",
      selector: (row) => row.employee_name,
      sortable: true,
    },
    {
      name: "Department",
      selector: (row) => row.department_name,
      sortable: true,
    },
  ];

  return (
    <div className="container-fluid mt-4">
      <div className="card shadow-sm mb-4">
        <h6 className="card-header d-flex align-items-center" style={{ color: "#1d3a8a", fontWeight: 600 }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="#1d3a8a" style={{ marginRight: "8px", verticalAlign: "-3px" }}>
            <path fillRule="evenodd" d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m1.679-4.493-1.335 2.226a.75.75 0 0 1-1.174.144l-.774-.773a.5.5 0 0 1 .708-.708l.547.548 1.017-1.694a.5.5 0 1 1 .858.257z"/>
            <path d="M11 5a3 3 0 1 1-6 0 3 3 0 0 1 6 0M8 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4"/>
            <path d="M8.256 14a4.5 4.5 0 0 1-.229-1.004H3c.001-.246.154-.986.832-1.664C4.484 10.68 5.711 10 8 10q.39 0 .74.025c.226-.341.496-.65.804-.918Q8.844 9.002 8 9c-5 0-6 3-6 4s1 1 1 1z"/>
          </svg>
          Unavailed Leave List
        </h6>

        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3 align-items-end">

              {/* From Date */}
              <div className="col-md-3">
                <label>From Date</label>
                <DatePicker
                  selected={new Date(filters.from_date)}
                  onChange={(date) =>
                    setFilters((prev) => ({
                      ...prev,
                      from_date: date
                        ? date.toLocaleDateString("en-CA")
                        : "",
                    }))
                  }
                  dateFormat="yyyy-MM-dd"
                  className="form-control"
                />
              </div>

              {/* To Date */}
              <div className="col-md-3">
                <label>To Date</label>
                <DatePicker
                  selected={new Date(filters.to_date)}
                  onChange={(date) =>
                    setFilters((prev) => ({
                      ...prev,
                      to_date: date
                        ? date.toLocaleDateString("en-CA")
                        : "",
                    }))
                  }
                  dateFormat="yyyy-MM-dd"
                  className="form-control"
                  minDate={new Date(filters.from_date)}
                />
              </div>

              {/* Department */}
              <div className="col-md-3">
                <label>Department</label>
                <Select
                  options={departments.map((dept) => ({
                    value: dept.department_id,
                    label: dept.department_name,
                  }))}
                  isClearable
                  placeholder="All Departments"
                  onChange={(selected) =>
                    setFilters((prev) => ({
                      ...prev,
                      dept_id: selected ? selected.value : 0,
                    }))
                  }
                />
              </div>

              {/* Button */}
              <div className="col-md-2">
                <button type="submit" className="btn btn-primary btn-sm" style={{ height: "38px" }} disabled={loading}>
                  {loading ? "Loading..." : "Get"}
                </button>
              </div>

            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-body">

          <div className="d-flex justify-content-between mb-3 align-items-center">
            <DownloadButtons
              data={exportData}
              filename="Unavailed_leave"
            />

            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
              <EntriesSelector
                entriesPerPage={perPage}
                setEntriesPerPage={setPerPage}
                setCurrentPage={setCurrentPage}
              />
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search..."
                style={{ width: "220px" }}
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
            </div>
          </div>

         <DataTable
  columns={columns}
  data={filteredRecords}
  progressPending={loading}
  highlightOnHover
  striped
  dense
  noDataComponent={
    <div className="p-3 text-danger fw-bold">
      No records found
    </div>
  }
/>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />

        </div>
      </div>
    </div>
  );
};

export default NoLeaveReport;
